import { useEffect, useRef } from "react";
import { Loader2 } from "lucide-react";
import { GuestCatalogSectionHeader } from "@/components/guest-catalog/GuestCatalogSectionHeader";
import { GuestCatalogSectionCard } from "@/components/guest-catalog/GuestCatalogSectionCard";
import { GuestCatalogItemView } from "@/components/guest-catalog/GuestCatalogItemView";
import { GuestCatalogEmptyState } from "@/components/guest-catalog/GuestCatalogEmptyState";
import { GuestCatalogResultsSkeleton } from "@/components/guest-catalog/GuestCatalogSkeleton";
import { getGuestCatalogEmptyMessage } from "@/components/guest-catalog/guestCatalogEmptyMessages";
import type { GuestCatalogViewMode } from "@/components/guest-catalog/guestCatalogViewMode";
import type { GuestOpacResourceSummary } from "@/lib/api/guest-opac";
import type { GuestCatalogType } from "@/hooks/useGuestOpacTenantSlug";

interface GuestCatalogResultsProps {
  items: GuestOpacResourceSummary[];
  total: number;
  viewMode: GuestCatalogViewMode;
  hasMore: boolean;
  loadingMore: boolean;
  loading?: boolean;
  searchQuery: string;
  type: GuestCatalogType;
  tenantSlug?: string;
  onLoadMore: () => void;
}

export function GuestCatalogResults({
  items,
  total,
  viewMode,
  hasMore,
  loadingMore,
  loading,
  searchQuery,
  type,
  tenantSlug,
  onLoadMore,
}: GuestCatalogResultsProps) {
  const sentinelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || !hasMore || loadingMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) onLoadMore();
      },
      { rootMargin: "240px", threshold: 0 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMore, loadingMore, onLoadMore, items.length]);

  const countLabel =
    total === 0
      ? "No matching resources"
      : `${total} result${total === 1 ? "" : "s"}${
          items.length < total ? ` · showing ${items.length}` : ""
        }`;

  const empty = getGuestCatalogEmptyMessage(searchQuery, type);

  return (
    <GuestCatalogSectionCard>
      <section className="space-y-4">
        <GuestCatalogSectionHeader title="Total Results" description={countLabel} />

        {loading && items.length === 0 ? (
          <GuestCatalogResultsSkeleton viewMode={viewMode} />
        ) : items.length === 0 ? (
          <GuestCatalogEmptyState title={empty.title} hint={empty.hint} />
        ) : (
          <GuestCatalogItemView items={items} viewMode={viewMode} tenantSlug={tenantSlug} />
        )}

        {items.length > 0 && (
          <div ref={sentinelRef} className="flex min-h-12 items-center justify-center py-4">
            {loadingMore && (
              <Loader2 className="h-6 w-6 animate-spin text-primary" aria-label="Loading more" />
            )}
            {!hasMore && <p className="text-xs text-gray-400">End of results</p>}
          </div>
        )}
      </section>
    </GuestCatalogSectionCard>
  );
}
